import { ApiClient } from '@grandstand-web/bally-web-shared/src/newPackages/ApiClient'
import {
  ConfigServiceContext,
  useContextUnconditionally,
} from '@grandstand-web/bally-web-shared/src/services/config/ConfigService'
import { useTealium } from '@grandstand-web/bally-web-shared/src/services/tealium/TealiumService'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import LoadingSpinner from '../../LoadingSpinner'

const Container = styled.div`
  width: 100%;
  height: 480px;
  background-color: white;
  padding: 0 10px;
  border-radius: 8px;

  @media (min-width: 375px) {
    padding: 0 16px;
  }

  @media (min-width: 560px) {
    height: 420px;
  }

  @media (min-width: 630px) {
    padding: 0 25px;
  }
`
const CreditCardPaymentMethodUpdateIFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`

interface PaymentMethodUpdateFomProps {
  paymentMethodId: string
  onSuccess?: () => void
  onCancel?: () => void
  isActive: boolean
}

const PaymentMethodUpdateForm = ({ paymentMethodId, onSuccess, onCancel, isActive }: PaymentMethodUpdateFomProps) => {
  const iFrameRef = useRef<HTMLIFrameElement>(null)
  const { currentConfig } = useContextUnconditionally(ConfigServiceContext)
  const [iFrameUrl, setiFrameUrl] = useState<string>()
  const tealium = useTealium()

  const createPaymentMethodUpdatePayload = (paymentMethodId: string) => {
    return {
      event_id: 'UPDATE_PAYMENT_METHOD_REQUEST',
      data: {
        UpdatePaymentMethod: {
          currentPaymentMethodId: paymentMethodId,
        },
        enableFields: 'true',
        created_at: new Date(), //unix time time,
      },
    }
  }

  const sendPaymentMethodUpdateReq = useCallback(() => {
    const message = createPaymentMethodUpdatePayload(paymentMethodId)
    iFrameRef?.current?.contentWindow?.postMessage(
      message,
      currentConfig.API.services.subscription_services.payment_integrations.evergent.post_message_target
    )
  }, [currentConfig, paymentMethodId])

  // Get the iframeUrl
  useEffect(() => {
    let cancelStateUpdate = false
    const getIframeUrl = async () => {
      const url =
        currentConfig.API.services.subscription_services.payment_integrations.evergent.iframe_url + '?action=update'
      const data = await ApiClient.convenientApiFetch({
        url: url,
        method: 'GET',
      })
      const { url: iFrameUrl } = await data.json()
      if (!cancelStateUpdate) {
        setiFrameUrl(iFrameUrl)
      }
    }
    getIframeUrl().catch(console.error)
    return () => {
      cancelStateUpdate = true
    }
  }, [currentConfig])

  useEffect(() => {
    const handleEvergentEvent = async (event: MessageEvent<any>) => {
      if (
        event.origin !==
        currentConfig.API.services.subscription_services.payment_integrations.evergent.post_message_target
      ) {
        return
      }
      switch (event.data.event_id) {
        case 'EV_IFRAME_UPDATE_READY':
          sendPaymentMethodUpdateReq()
          return
        case 'EVERGENT_ERROR':
          const { errorCode, errorDesc } = event.data.data.ErrorResponse
          // TODO: Hook into any observability/durability mechanism that middleware exposes when it is available.
          return
        case 'EVERGENT_CC_UPDATE_SUCCESS':
          if (onSuccess) {
            onSuccess()
          }
          if (isActive) {
            tealium.updatePaymentMethod({
              payment_method: 'credit card',
            })
          }
          return
        case 'EVERGENT_CC_UPDATE_CANCEL':
          if (onCancel) {
            onCancel()
          }
          return
      }
    }
    window.addEventListener('message', handleEvergentEvent, false)
    return () => {
      window.removeEventListener('message', handleEvergentEvent)
    }
  }, [
    tealium,
    currentConfig.API.services.subscription_services.payment_integrations.evergent.post_message_target,
    onCancel,
    onSuccess,
    sendPaymentMethodUpdateReq,
    isActive,
  ])

  if (iFrameUrl) {
    return (
      <Container>
        <CreditCardPaymentMethodUpdateIFrame ref={iFrameRef} src={iFrameUrl} />
      </Container>
    )
  }
  return <LoadingSpinner />
}
export default PaymentMethodUpdateForm
